import React, {useCallback, useEffect, useRef} from 'react';
import styled from 'styled-components';
import {mobile} from 'shared/Breakpoints';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {VideoInfo} from 'shared/components/VideoPlayer/entity/VideoInfo';
import {Playlist} from 'shared/components/VideoPlayer/Playlist';

const WATCHED_THRESHOLD = 0.9;

interface PlayerProps {
    videoInfo: VideoInfo;
    videos: VideoInfo[];
    watched: Record<string, boolean>;
    resetVideo: () => void;
    setVideoInfo: (video: VideoInfo) => void;
    onWatched: (video: VideoInfo) => void;
    onPlayGuide: () => void;
    onClose: () => void;
}

export const Player = ({
    videoInfo,
    videos,
    watched,
    resetVideo,
    setVideoInfo,
    onWatched,
    onPlayGuide,
    onClose,
}: PlayerProps) => {
    const isSmallDevice = isDeviceSmall();
    const reported = useRef(false);
    const index = videos.findIndex((video) => video.id === videoInfo.id);
    const previous = index > 0 ? videos[index - 1] : undefined;
    const next = videos[(index + 1) % videos.length];

    useEffect(() => {
        reported.current = false;
    }, [videoInfo.id]);

    const markWatched = useCallback(() => {
        if (!reported.current) {
            reported.current = true;
            onWatched(videoInfo);
        }
    }, [onWatched, videoInfo]);

    const handleTimeUpdate = useCallback(
        (event: React.SyntheticEvent<HTMLVideoElement>) => {
            const {currentTime, duration} = event.currentTarget;

            if (duration && currentTime / duration >= WATCHED_THRESHOLD) {
                markWatched();
            }
        },
        [markWatched]
    );

    const playPrevious = useCallback(() => {
        if (previous) setVideoInfo(previous);
    }, [previous, setVideoInfo]);

    const playNext = useCallback(() => {
        setVideoInfo(next);
    }, [next, setVideoInfo]);

    return (
        <Layout>
            <Main>
                <BackLink type="button" onClick={resetVideo}>
                    ‹ Back to help
                </BackLink>
                <Counter>
                    Video {index + 1} of {videos.length}
                </Counter>
                <Title>{videoInfo.title}</Title>
                <VideoContainer>
                    <VideoElement
                        key={videoInfo.id}
                        autoPlay
                        controls
                        src={videoInfo.video}
                        onTimeUpdate={handleTimeUpdate}
                        onEnded={markWatched}>
                        Your browser does not support the video tag
                    </VideoElement>
                </VideoContainer>
                <Description>{videoInfo.description}</Description>
                <Controls>
                    <SecondaryButton
                        type="button"
                        disabled={!previous}
                        onClick={playPrevious}>
                        ‹ Previous
                    </SecondaryButton>
                    <PrimaryButton type="button" onClick={playNext}>
                        Next: {next.title} ›
                    </PrimaryButton>
                </Controls>
            </Main>
            {isSmallDevice ? null : (
                <Sidebar>
                    <SidebarHeader>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={onClose}
                        />
                    </SidebarHeader>
                    <Playlist
                        videos={videos}
                        watched={watched}
                        selectedVideo={videoInfo}
                        playVideo={setVideoInfo}
                    />
                    <GuideCard>
                        <GuideIcon viewBox="0 0 24 24" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M15.5 8.5L13.4 13.4L8.5 15.5L10.6 10.6Z" />
                        </GuideIcon>
                        <div>
                            <GuideTitle>Find your way around</GuideTitle>
                            <GuideMeta>20 sec guide</GuideMeta>
                        </div>
                        <SecondaryButton
                            type="button"
                            onClick={onPlayGuide}
                            aria-label="Play the navigation guide">
                            Play
                        </SecondaryButton>
                    </GuideCard>
                </Sidebar>
            )}
        </Layout>
    );
};

const Layout = styled.div`
    display: flex;
    margin: -1rem;

    ${mobile`margin: 0;`}
`;

const Main = styled.div`
    flex: 1;
    min-width: 0;
    padding: 24px 28px;

    ${mobile`padding: 0;`}
`;

const BackLink = styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    font-weight: 600;
    color: ${({theme}) => theme.colors.secondary.main};

    &:hover {
        text-decoration: underline;
    }
`;

const Counter = styled.div`
    margin-top: 14px;
    font-size: 0.75em;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #60788e;
`;

const Title = styled.div`
    margin: 2px 0 14px;
    font-size: 1.6em;
    font-weight: 600;
    color: ${({theme}) => theme.colors.primary.main};

    ${mobile`font-size: 1.3em;`}
`;

const VideoContainer = styled.div`
    position: relative;
    width: 100%;
    padding-top: 56.25%;
    overflow: hidden;
    border-radius: 12px;
    background: #1e2a3a;
`;

const VideoElement = styled.video`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
`;

const Description = styled.p`
    margin: 14px 0 18px;
    color: #535353;
`;

const Controls = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
`;

const PrimaryButton = styled.button`
    border: 0;
    border-radius: 9px;
    padding: 10px 18px;
    font-weight: 600;
    color: #fff;
    background: ${({theme}) => theme.colors.primary.main};
`;

const SecondaryButton = styled.button`
    border: 1px solid #dce6ed;
    border-radius: 9px;
    padding: 8px 16px;
    font-weight: 600;
    color: ${({theme}) => theme.colors.primary.main};
    background: #fff;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const Sidebar = styled.aside`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 300px;
    flex-shrink: 0;
    padding: 16px;
    background: #f6f9fc;
    border-left: 1px solid #dce6ed;
    border-top-right-radius: 15px;
    border-bottom-right-radius: 15px;
`;

const SidebarHeader = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const GuideCard = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: auto;
    padding: 12px;
    background: #fff;
    border: 1px solid #dce6ed;
    border-radius: 12px;

    > div {
        flex: 1;
    }
`;

const GuideIcon = styled.svg`
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    fill: none;
    stroke: ${({theme}) => theme.colors.secondary.main};
    stroke-width: 1.8;
    stroke-linejoin: round;

    path {
        fill: ${({theme}) => theme.colors.secondary.main};
        fill-opacity: 0.25;
    }
`;

const GuideTitle = styled.div`
    font-weight: 600;
    font-size: 0.9em;
    color: ${({theme}) => theme.colors.primary.main};
`;

const GuideMeta = styled.div`
    font-size: 0.8em;
    color: #60788e;
`;
